import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { workOrdersApi, uploadApi, teamApi } from '../lib/api';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ClipboardList, Plus, ArrowRight, X, GripVertical, Calendar as CalendarIcon, Upload, Link2, ChevronDown, Eye, Film, Camera, ChevronLeft, ChevronRight, LayoutGrid, CalendarDays, CalendarRange, FileText, Check, MessageSquare, ExternalLink, Trash2, Download } from 'lucide-react';

const API_URL = '';

const WORK_TYPES = [
  { id: 'video_vertical', label: 'Video Vertical', icon: Film },
  { id: 'video_horizontal', label: 'Video Horizontal', icon: Film },
  { id: 'grafico_digital', label: 'Grafico Digital', icon: Camera },
  { id: 'grafico_imprimible', label: 'Grafico Imprimible', icon: Camera },
  { id: 'idea_escrita', label: 'Idea Escrita', icon: FileText },
  { id: 'audio', label: 'Audio', icon: Film },
];

const STATUS_COLS = [
  { id: 'pending', label: 'Pendiente', color: 'bg-yellow-500' },
  { id: 'in_progress', label: 'Atendiendo', color: 'bg-blue-500' },
  { id: 'proposals_submitted', label: 'Propuestas', color: 'bg-purple-500' },
  { id: 'completed', label: 'Completado', color: 'bg-green-500' },
  { id: 'approved_by_client', label: 'Aprobado', color: 'bg-emerald-500' },
  { id: 'rejected', label: 'Rechazada', color: 'bg-red-500' },
];

const CLIENT_STATUS_COLS = [
  { id: 'pending', label: 'Pendientes de atender', color: 'bg-yellow-500' },
  { id: 'in_progress', label: 'Atendiendo', color: 'bg-blue-500' },
  { id: 'completed', label: 'Completadas', color: 'bg-green-500' },
  { id: 'approved_by_client', label: 'Aprobadas', color: 'bg-emerald-500' },
];

// ==================== LIGHTBOX COMPONENT ====================
const Lightbox = ({ files, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex || 0);
  const file = files[idx];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(i => Math.min(i + 1, files.length - 1));
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [files.length, onClose]);

  if (!file) return null;
  const src = file.url?.startsWith('http') ? file.url : `${API_URL}${file.url}`;
  const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.url || '') || file.file_type === 'image';
  const isVideo = /\.(mp4|mov|avi|webm)$/i.test(file.url || '') || file.file_type === 'video';
  const isPdf = /\.pdf$/i.test(file.url || '') || file.file_type === 'pdf';

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white z-10" data-testid="lightbox-close"><X size={32} /></button>
      {files.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); setIdx(i => Math.max(i - 1, 0)); }} className="absolute left-4 text-white/60 hover:text-white z-10"><ChevronLeft size={40} /></button>
          <button onClick={e => { e.stopPropagation(); setIdx(i => Math.min(i + 1, files.length - 1)); }} className="absolute right-4 text-white/60 hover:text-white z-10"><ChevronRight size={40} /></button>
        </>
      )}
      <div onClick={e => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh] flex items-center justify-center">
        {isImg && <img src={src} alt={file.name || ''} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" />}
        {isVideo && <video src={src} controls autoPlay className="max-w-[90vw] max-h-[85vh] rounded-lg" />}
        {isPdf && <iframe src={src} title="PDF" className="w-[80vw] h-[85vh] rounded-lg bg-white" />}
        {!isImg && !isVideo && !isPdf && (
          <div className="text-center text-white p-8">
            <FileText size={64} className="mx-auto mb-4 text-zinc-400" />
            <p className="mb-4">{file.name}</p>
            <a href={src} target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300">Descargar archivo</a>
          </div>
        )}
      </div>
      <div className="absolute bottom-4 text-white/60 text-sm">{idx + 1} / {files.length}</div>
    </div>
  );
};

const WorkOrdersPage = () => {
  const { selectedClientId, clients } = useClient();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isClient = user?.role === 'client';
  const isAdmin = user?.role === 'admin';
  const activeCols = isClient ? CLIENT_STATUS_COLS : STATUS_COLS;

  const [orders, setOrders] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ assigned_to: '' });
  const [viewMode, setViewMode] = useState('kanban');
  const [lightbox, setLightbox] = useState(null);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editDatePickerOpen, setEditDatePickerOpen] = useState(false);
  const [driveLinkInput, setDriveLinkInput] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); d.setHours(0,0,0,0); return d;
  });
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  const emptyForm = () => ({
    title: '', description: '', client_id: selectedClientId || '', priority: 'medium', due_date: null, work_type: '', inspiration: [],
  });
  const [form, setForm] = useState(emptyForm());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, teamRes] = await Promise.all([
        workOrdersApi.getAll(isClient ? undefined : (selectedClientId === 'all' ? undefined : selectedClientId)),
        isClient ? Promise.resolve({ data: [] }) : teamApi.getMembers().catch(() => ({ data: [] }))
      ]);
      setOrders(ordersRes.data);
      setTeam(teamRes.data);
    } catch { toast.error('Error al cargar datos'); }
    setLoading(false);
  }, [selectedClientId, isClient]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, due_date: form.due_date ? new Date(form.due_date).toISOString() : null };
      if (isClient) delete data.client_id;
      await workOrdersApi.create(data);
      toast.success('Orden creada');
      setDialogOpen(false);
      setForm(emptyForm());
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const woId = e.dataTransfer.getData('woId');
    if (!woId) return;
    try {
      await workOrdersApi.updateStatus(woId, newStatus);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (id) => {
    try { await workOrdersApi.delete(id); fetchData(); toast.success('Eliminada'); } catch { toast.error('Error'); }
  };

  const openEditDialog = (order) => {
    setEditForm({
      title: order.title || '', description: order.description || '', priority: order.priority || 'medium',
      work_type: order.work_type || '', due_date: order.due_date ? new Date(order.due_date) : null,
      client_id: order.client_id || '',
    });
    setSelectedOrder(order);
    setEditDialogOpen(true);
    setDetailDialogOpen(false);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    try {
      const data = { ...editForm, due_date: editForm.due_date ? new Date(editForm.due_date).toISOString() : null };
      await workOrdersApi.update(selectedOrder.work_order_id, data);
      toast.success('Orden actualizada');
      setEditDialogOpen(false);
      fetchData();
    } catch { toast.error('Error al actualizar'); }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    try {
      await workOrdersApi.convert(selectedOrder.work_order_id, { assigned_to: convertForm.assigned_to });
      toast.success('Orden asignada como tarea');
      setConvertDialogOpen(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleUploadProposal = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedOrder) return;
    const existing = selectedOrder.proposals || [];
    if (existing.length + files.length > 3) { toast.error('Maximo 3 propuestas'); return; }
    setUploading(true);
    try {
      const newProposals = [...existing];
      for (const file of files) {
        const res = await uploadApi.upload(file);
        newProposals.push({ url: res.data.url, name: res.data.filename, file_type: res.data.file_type });
      }
      await workOrdersApi.uploadProposals(selectedOrder.work_order_id, { proposals: newProposals, drive_links: selectedOrder.proposals_drive_links || [] });
      setSelectedOrder({ ...selectedOrder, proposals: newProposals });
      toast.success('Propuesta subida');
      fetchData();
    } catch { toast.error('Error al subir'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleAddDriveLink = () => {
    if (!driveLinkInput.trim() || !selectedOrder) return;
    const links = [...(selectedOrder.proposals_drive_links || []), { url: driveLinkInput.trim(), name: driveLinkInput.includes('drive.google') ? 'Google Drive' : 'Dropbox' }];
    workOrdersApi.uploadProposals(selectedOrder.work_order_id, { proposals: selectedOrder.proposals || [], drive_links: links });
    setSelectedOrder({ ...selectedOrder, proposals_drive_links: links });
    setDriveLinkInput('');
    toast.success('Enlace agregado');
  };

  const handleSubmitProposals = async () => {
    if (!selectedOrder) return;
    try {
      await workOrdersApi.submitProposals(selectedOrder.work_order_id);
      toast.success('Propuestas enviadas para revision');
      setDetailDialogOpen(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleAdminApprove = async () => {
    if (!selectedOrder) return;
    try {
      await workOrdersApi.adminApprove(selectedOrder.work_order_id);
      toast.success('Aprobado. Notificacion enviada al cliente');
      setDetailDialogOpen(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleClientApprove = async (proposalIdx) => {
    if (!selectedOrder) return;
    try {
      await workOrdersApi.clientApprove(selectedOrder.work_order_id, { selected_proposal: proposalIdx });
      toast.success('Orden aprobada');
      setDetailDialogOpen(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleClientReject = async () => {
    if (!selectedOrder || !rejectFeedback.trim()) return;
    try {
      await workOrdersApi.clientReject(selectedOrder.work_order_id, { feedback: rejectFeedback });
      toast.success('Observaciones enviadas');
      setRejectDialogOpen(false);
      setDetailDialogOpen(false);
      setRejectFeedback('');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteProposal = async (proposalIdx) => {
    if (!selectedOrder) return;
    try {
      const res = await workOrdersApi.deleteProposal(selectedOrder.work_order_id, proposalIdx);
      setSelectedOrder({ ...selectedOrder, proposals: res.data.proposals });
      toast.success('Propuesta eliminada');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const openDetail = (order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
    setDriveLinkInput('');
  };

  const canManage = isAdmin || user?.position?.toLowerCase()?.includes('produccion');
  const priorityLabel = { low: 'Baja', medium: 'Media', high: 'Alta' };
  const priorityColor = { low: 'bg-green-500/20 text-green-400', medium: 'bg-orange-500/20 text-orange-400', high: 'bg-red-500/20 text-red-400' };
  const workTypeLabel = WORK_TYPES.reduce((acc, t) => ({ ...acc, [t.id]: t.label }), {});
  const statusLabel = (s) => [...STATUS_COLS, ...CLIENT_STATUS_COLS].find(c => c.id === s)?.label || s;
  const statusColor = (s) => [...STATUS_COLS, ...CLIENT_STATUS_COLS].find(c => c.id === s)?.color || 'bg-zinc-500';

  const OrderCard = ({ order }) => (
    <div draggable={!isClient} onDragStart={e => { e.dataTransfer.setData('woId', order.work_order_id); }}
      className={cn('p-4 rounded-lg border transition-colors group cursor-pointer', isDark ? 'bg-[#1A1A1A] border-white/5 hover:border-orange-500/30' : 'bg-white border-zinc-200 hover:border-orange-300')}
      onClick={() => openDetail(order)} data-testid={`work-order-${order.work_order_id}`}>
      <div className="flex items-start gap-2 mb-2">
        {!isClient && <GripVertical className="text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 cursor-grab" size={16} />}
        <div className="flex-1 min-w-0">
          {order.folio && <span className="text-[10px] text-orange-400 font-mono block">{order.folio}</span>}
          <h4 className={cn('font-medium text-sm truncate', isDark ? 'text-white' : 'text-zinc-900')}>{order.title}</h4>
          <p className={cn('text-xs mt-1 line-clamp-2', isDark ? 'text-zinc-500' : 'text-zinc-400')}>{order.description}</p>
        </div>
        {!isClient && <button onClick={e => { e.stopPropagation(); handleDelete(order.work_order_id); }} className="text-zinc-600 hover:text-red-400" data-testid={`delete-wo-${order.work_order_id}`}><X size={14} /></button>}
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <span className={cn('px-2 py-0.5 rounded text-xs', priorityColor[order.priority])}>{priorityLabel[order.priority]}</span>
        {order.work_type && <span className={cn('px-2 py-0.5 rounded text-xs', isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-50 text-purple-600')}>{workTypeLabel[order.work_type] || order.work_type}</span>}
        {order.due_date && <span className={cn('text-xs', isDark ? 'text-zinc-500' : 'text-zinc-400')}>{format(new Date(order.due_date), 'd MMM HH:mm', { locale: es })}</span>}
        {order.proposals?.length > 0 && <span className="text-xs text-purple-400">{order.proposals.length} prop.</span>}
      </div>
      {order.client_feedback && <p className={cn('text-xs mt-2 p-2 rounded', isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600')}>Obs: {order.client_feedback}</p>}
      {canManage && order.status === 'pending' && (
        <Button size="sm" onClick={e => { e.stopPropagation(); setSelectedOrder(order); setConvertDialogOpen(true); }} className="bg-orange-500 hover:bg-orange-600 w-full mt-3 h-7 text-xs" data-testid={`convert-btn-${order.work_order_id}`}>
          <ArrowRight size={12} className="mr-1" /> Asignar a empleado
        </Button>
      )}
    </div>
  );

  // ==================== CLIENT DASHBOARD VIEW ====================
  if (isClient && viewMode === 'kanban') {
    return (
      <DashboardLayout title="Mis Ordenes de Trabajo">
        <div className="space-y-6" data-testid="wo-page">
          <div className="flex justify-between items-center">
            <p className={cn('text-sm', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Solicita servicios y sigue el estado de tus ordenes</p>
            <div className="flex gap-2">
              <Button onClick={() => setDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="create-wo-btn"><Plus size={16} className="mr-1" /> Nueva Orden</Button>
            </div>
          </div>

          {/* Client Orders Table */}
          <Card className={cn(isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
            <CardHeader>
              <CardTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Ultimas Ordenes</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8"><ClipboardList className="mx-auto mb-3 text-zinc-400" size={40} /><p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Aun no tienes ordenes</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className={cn('border-b', isDark ? 'border-white/5' : 'border-zinc-100')}>
                      <th className={cn('text-left py-3 px-2 font-medium', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Folio</th>
                      <th className={cn('text-left py-3 px-2 font-medium', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Titulo</th>
                      <th className={cn('text-left py-3 px-2 font-medium', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Estatus</th>
                      <th className={cn('text-left py-3 px-2 font-medium', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Creacion</th>
                      <th className={cn('text-left py-3 px-2 font-medium', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Fecha Limite</th>
                      <th className="py-3 px-2"></th>
                    </tr></thead>
                    <tbody>
                      {orders.map(o => (
                        <tr key={o.work_order_id} className={cn('border-b cursor-pointer transition-colors', isDark ? 'border-white/5 hover:bg-white/5' : 'border-zinc-50 hover:bg-zinc-50')} onClick={() => openDetail(o)} data-testid={`wo-row-${o.work_order_id}`}>
                          <td className="py-3 px-2"><span className="text-orange-400 font-mono text-xs">{o.folio || '-'}</span></td>
                          <td className={cn('py-3 px-2 font-medium', isDark ? 'text-white' : 'text-zinc-900')}>{o.title}</td>
                          <td className="py-3 px-2"><Badge className={cn('text-[10px]', statusColor(o.status) + '/20 text-white')}>{statusLabel(o.status)}</Badge></td>
                          <td className={cn('py-3 px-2 text-xs', isDark ? 'text-zinc-400' : 'text-zinc-500')}>{format(new Date(o.created_at), 'd MMM yyyy', { locale: es })}</td>
                          <td className={cn('py-3 px-2 text-xs', isDark ? 'text-zinc-400' : 'text-zinc-500')}>{o.due_date ? format(new Date(o.due_date), 'd MMM HH:mm', { locale: es }) : '-'}</td>
                          <td className="py-3 px-2"><Eye size={14} className="text-zinc-400" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client Kanban Board */}
          <div className={cn('grid gap-4', `grid-cols-1 md:grid-cols-${CLIENT_STATUS_COLS.length}`)}>
            {CLIENT_STATUS_COLS.map(col => (
              <div key={col.id} className="space-y-2" data-testid={`wo-column-${col.id}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${col.color}`} />
                  <h3 className={cn('font-medium text-sm', isDark ? 'text-white' : 'text-zinc-800')}>{col.label}</h3>
                  <span className="text-xs text-zinc-500 ml-auto">{orders.filter(o => o.status === col.id).length}</span>
                </div>
                <div className={cn('space-y-2 min-h-[120px] p-2 rounded-xl border', isDark ? 'bg-[#141414] border-white/5' : 'bg-zinc-50 border-zinc-100')}>
                  {orders.filter(o => o.status === col.id).map(o => <OrderCard key={o.work_order_id} order={o} />)}
                </div>
              </div>
            ))}
          </div>

          {/* Create Dialog */}
          {renderCreateDialog()}
          {/* Detail Dialog */}
          {renderDetailDialog()}
          {/* Reject Dialog */}
          {renderRejectDialog()}
          {/* Lightbox */}
          {lightbox && <Lightbox files={lightbox.files} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
        </div>
      </DashboardLayout>
    );
  }

  // ==================== ADMIN/TEAM VIEW ====================
  function renderCreateDialog() {
    return (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={cn('max-w-lg max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Nueva Orden de Trabajo</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Titulo *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className={isDark ? 'bg-[#1A1A1A] border-transparent text-white' : ''} data-testid="wo-title-input" /></div>
            <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Descripcion *</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className={cn('min-h-[80px]', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : '')} data-testid="wo-desc-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              {!isClient && selectedClientId === 'all' && (
                <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Cliente</Label>
                  <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                    <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Prioridad</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''}><SelectValue /></SelectTrigger>
                  <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}><SelectItem value="low">Baja</SelectItem><SelectItem value="medium">Media</SelectItem><SelectItem value="high">Alta</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Tipo de trabajo</Label>
                <Select value={form.work_type} onValueChange={v => setForm({...form, work_type: v})}>
                  <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>{WORK_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Fecha y Hora de Entrega</Label>
              <div className="flex gap-2">
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('flex-1 justify-start text-left font-normal', isDark ? 'bg-[#1A1A1A] border-transparent' : '', !form.due_date && 'text-zinc-500')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />{form.due_date ? format(new Date(form.due_date), 'PPP', { locale: es }) : 'Selecciona fecha'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className={cn('w-auto p-0', isDark ? 'bg-[#1A1A1A] border-white/10' : '')}><Calendar mode="single" selected={form.due_date ? new Date(form.due_date) : undefined} onSelect={d => { if (d) { const existing = form.due_date ? new Date(form.due_date) : new Date(); d.setHours(existing.getHours(), existing.getMinutes()); } setForm({...form, due_date: d}); setDatePickerOpen(false); }} locale={es} initialFocus /></PopoverContent>
                </Popover>
                <Input type="time" value={form.due_date ? format(new Date(form.due_date), 'HH:mm') : ''} onChange={e => { const [h, m] = (e.target.value || '00:00').split(':').map(Number); const d = form.due_date ? new Date(form.due_date) : new Date(); d.setHours(h, m); setForm({...form, due_date: d}); }} className={cn('w-28', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : '')} data-testid="wo-time-input" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="wo-submit-btn">Crear Orden</Button>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // ==================== DETAIL DIALOG ====================
  function renderDetailDialog() {
    if (!selectedOrder) return null;
    const o = selectedOrder;
    const proposals = o.proposals || [];
    const driveLinks = o.proposals_drive_links || [];
    const isMyTask = !isClient && !isAdmin; // regular employee viewing

    return (
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={cn('max-w-2xl max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')} data-testid="wo-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                {o.folio && <span className="text-orange-400 font-mono text-xs">{o.folio}</span>}
                <DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>{o.title}</DialogTitle>
              </div>
              <div className="flex items-center gap-2">
                {!isClient && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(o)} className={isDark ? 'border-white/10 text-zinc-300' : 'border-zinc-200 text-zinc-700'} data-testid="wo-edit-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/></svg>
                      Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { handleDelete(o.work_order_id); setDetailDialogOpen(false); }} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="wo-delete-btn">
                      <Trash2 size={14} />
                    </Button>
                  </>
                )}
                <Badge className={cn('shrink-0', statusColor(o.status) + '/20')}>{statusLabel(o.status)}</Badge>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p className={cn('text-sm', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{o.description}</p>
            
            <div className="flex flex-wrap gap-3 text-xs">
              <span className={cn('px-2 py-1 rounded', priorityColor[o.priority])}>{priorityLabel[o.priority]}</span>
              {o.work_type && <span className={cn('px-2 py-1 rounded', isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-50 text-purple-600')}>{workTypeLabel[o.work_type]}</span>}
              {o.due_date && <span className={cn('px-2 py-1 rounded', isDark ? 'bg-white/5 text-zinc-300' : 'bg-zinc-100 text-zinc-600')}>Entrega: {format(new Date(o.due_date), 'd MMM yyyy HH:mm', { locale: es })}</span>}
              <span className={cn('px-2 py-1 rounded', isDark ? 'bg-white/5 text-zinc-400' : 'bg-zinc-100 text-zinc-500')}>Creado: {format(new Date(o.created_at), 'd MMM yyyy', { locale: es })}</span>
            </div>

            {/* Client Feedback (observations) */}
            {o.client_feedback && (
              <div className={cn('p-3 rounded-lg border', isDark ? 'bg-red-500/5 border-red-500/20' : 'bg-red-50 border-red-200')}>
                <p className="text-xs font-medium text-red-400 mb-1">Observaciones del cliente:</p>
                <p className={cn('text-sm', isDark ? 'text-red-300' : 'text-red-600')}>{o.client_feedback}</p>
              </div>
            )}

            {/* Proposals Section */}
            {proposals.length > 0 && (
              <div>
                <Label className={cn('text-xs mb-2 block', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Propuestas ({proposals.length}/3)</Label>
                <div className="grid grid-cols-3 gap-3">
                  {proposals.map((p, i) => {
                    const src = p.url?.startsWith('http') ? p.url : `${API_URL}${p.url}`;
                    const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(p.url || '') || p.file_type === 'image';
                    const isApproved = o.approved_version?.proposal_index === i && o.status === 'approved_by_client';
                    const canDeleteProposal = !isClient && (o.status === 'in_progress' || o.status === 'proposals_submitted');
                    return (
                      <div key={i} className={cn('rounded-lg overflow-hidden border cursor-pointer transition-all relative group', 
                        isApproved ? 'ring-2 ring-emerald-500 border-emerald-500' : 'hover:ring-2 hover:ring-orange-500/50',
                        isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}
                        onClick={() => setLightbox({ files: proposals, index: i })} data-testid={`proposal-${i}`}>
                        {isApproved && (
                          <div className="absolute top-1 left-1 z-10 bg-emerald-500 text-white rounded-full p-0.5" data-testid={`approved-badge-${i}`}>
                            <Check size={12} />
                          </div>
                        )}
                        {canDeleteProposal && (
                          <button onClick={e => { e.stopPropagation(); handleDeleteProposal(i); }}
                            className="absolute top-1 right-1 z-10 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-proposal-${i}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                        {isImg ? <img src={src} alt={p.name} className="w-full h-28 object-cover" onError={e => { e.target.style.display='none'; }} /> : (
                          <div className="w-full h-28 flex items-center justify-center"><FileText size={24} className="text-zinc-400" /></div>
                        )}
                        <div className={cn('px-2 py-1.5', isDark ? '' : '')}>
                          <p className={cn('text-[10px] truncate font-medium', isDark ? 'text-zinc-300' : 'text-zinc-700')} title={p.name || `Propuesta ${i+1}`}>{p.name || `Propuesta ${i + 1}`}</p>
                          {isApproved && <p className="text-[9px] text-emerald-400 font-medium">Aprobada</p>}
                          <a href={`${API_URL}/api/download/${(p.url || '').split('/').pop()}`} download onClick={e => e.stopPropagation()} className="text-zinc-400 hover:text-orange-400 inline-block mt-0.5" data-testid={`download-proposal-${i}`}><Download size={12} /></a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Drive/Dropbox links */}
            {driveLinks.length > 0 && (
              <div>
                <Label className={cn('text-xs mb-2 block', isDark ? 'text-zinc-500' : 'text-zinc-400')}>Enlaces externos</Label>
                <div className="space-y-1">
                  {driveLinks.map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noreferrer" className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors', isDark ? 'bg-white/5 hover:bg-white/10 text-zinc-300' : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-700')}>
                      <ExternalLink size={14} className="text-orange-400" /> {l.name || l.url}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Approval signature */}
            {o.approved_version && (
              <div className={cn('p-3 rounded-lg border', isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-green-50 border-green-200')}>
                <p className="text-xs font-medium text-emerald-400 mb-1">Aprobacion Final</p>
                <p className={cn('text-sm', isDark ? 'text-emerald-300' : 'text-green-700')}>Aprobado por: {o.approved_version.approved_by_name}</p>
                {o.approved_version.proposal_name && (
                  <p className={cn('text-sm', isDark ? 'text-emerald-300' : 'text-green-700')}>Propuesta aprobada: <span className="font-medium">{o.approved_version.proposal_name}</span></p>
                )}
                <p className={cn('text-xs', isDark ? 'text-emerald-400/60' : 'text-green-500')}>{format(new Date(o.approved_version.approved_at), 'd MMM yyyy HH:mm', { locale: es })}</p>
              </div>
            )}

            {/* EMPLOYEE: Upload proposals (when in_progress) */}
            {!isClient && (o.status === 'in_progress' || o.status === 'proposals_submitted') && (
              <div className={cn('p-4 rounded-lg border', isDark ? 'bg-blue-500/5 border-blue-500/20' : 'bg-blue-50 border-blue-200')}>
                <Label className="text-blue-400 text-sm font-medium mb-3 block"><Upload size={14} className="inline mr-1" />Cargar Propuestas (max 3)</Label>
                <div className="flex gap-2 mb-3">
                  <label className={cn('flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed cursor-pointer transition-colors', isDark ? 'border-blue-500/30 hover:border-blue-500/60 bg-[#1A1A1A]' : 'border-blue-300 hover:border-blue-500 bg-white')}>
                    <Upload size={16} className="text-blue-400" />
                    <span className="text-sm text-blue-400">{uploading ? 'Subiendo...' : 'Subir archivos'}</span>
                    <input type="file" accept="image/*,video/*,.pdf" multiple onChange={handleUploadProposal} className="hidden" data-testid="proposal-upload-input" />
                  </label>
                </div>
                <div className="flex gap-2">
                  <Input value={driveLinkInput} onChange={e => setDriveLinkInput(e.target.value)} placeholder="Enlace Google Drive / Dropbox" className={cn('flex-1', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : '')} data-testid="drive-link-input" />
                  <Button size="sm" onClick={handleAddDriveLink} disabled={!driveLinkInput.trim()} className="bg-blue-500 hover:bg-blue-600"><Link2 size={14} /></Button>
                </div>
                {proposals.length > 0 && o.status === 'in_progress' && (
                  <Button onClick={handleSubmitProposals} className="w-full mt-3 bg-purple-600 hover:bg-purple-700" data-testid="submit-proposals-btn">
                    <Check size={14} className="mr-1" /> Generar Propuestas
                  </Button>
                )}
              </div>
            )}

            {/* ADMIN: Approve proposals */}
            {isAdmin && o.status === 'proposals_submitted' && proposals.length > 0 && (
              <Button onClick={handleAdminApprove} className="w-full bg-green-600 hover:bg-green-700" data-testid="admin-approve-btn">
                <Check size={14} className="mr-1" /> Aprobar y Enviar al Cliente
              </Button>
            )}

            {/* CLIENT: Approve or Reject */}
            {isClient && o.status === 'completed' && proposals.length > 0 && (
              <div className="space-y-3">
                <p className={cn('text-sm font-medium', isDark ? 'text-white' : 'text-zinc-900')}>Selecciona la propuesta que apruebas:</p>
                <div className="grid grid-cols-3 gap-3">
                  {proposals.map((p, i) => (
                    <Button key={i} variant="outline" onClick={() => handleClientApprove(i)} className={cn('h-auto p-2 flex flex-col items-center gap-1', isDark ? 'border-white/10 hover:border-green-500/50' : 'hover:border-green-400')} data-testid={`approve-proposal-${i}`}>
                      <Check size={16} className="text-green-400" />
                      <span className="text-[10px] truncate w-full text-center" title={p.name || `Propuesta ${i+1}`}>{p.name || `Propuesta ${i + 1}`}</span>
                    </Button>
                  ))}
                </div>
                <Button variant="outline" onClick={() => setRejectDialogOpen(true)} className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="client-reject-btn">
                  <MessageSquare size={14} className="mr-1" /> Enviar Observaciones
                </Button>
              </div>
            )}

            {/* ADMIN: Assign pending orders */}
            {canManage && o.status === 'pending' && (
              <Button onClick={() => { setConvertDialogOpen(true); }} className="w-full bg-orange-500 hover:bg-orange-600">
                <ArrowRight size={14} className="mr-1" /> Asignar a Empleado
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  function renderRejectDialog() {
    return (
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className={cn('max-w-md', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Observaciones</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Textarea value={rejectFeedback} onChange={e => setRejectFeedback(e.target.value)} placeholder="Describe que cambios necesitas..." className={cn('min-h-[100px]', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : '')} data-testid="reject-feedback-input" />
            <Button onClick={handleClientReject} disabled={!rejectFeedback.trim()} className="w-full bg-red-500 hover:bg-red-600" data-testid="reject-submit-btn">Enviar Observaciones</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DashboardLayout title="Ordenes de Trabajo">
      <div className="space-y-6" data-testid="wo-page">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={cn('flex gap-1 rounded-lg p-1 border', isDark ? 'bg-[#141414] border-white/5' : 'bg-zinc-100 border-zinc-200')}>
              <button onClick={() => setViewMode('kanban')} className={cn('px-3 py-1.5 rounded-md text-xs font-medium transition-colors', viewMode === 'kanban' ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500')} data-testid="view-kanban"><LayoutGrid size={14} className="inline mr-1" />Kanban</button>
              <button onClick={() => setViewMode('weekly')} className={cn('px-3 py-1.5 rounded-md text-xs font-medium transition-colors', viewMode === 'weekly' ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500')} data-testid="view-weekly"><CalendarDays size={14} className="inline mr-1" />Semanal</button>
              <button onClick={() => setViewMode('monthly')} className={cn('px-3 py-1.5 rounded-md text-xs font-medium transition-colors', viewMode === 'monthly' ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500')} data-testid="view-monthly"><CalendarRange size={14} className="inline mr-1" />Mensual</button>
            </div>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="create-wo-btn"><Plus size={16} className="mr-1" /> Nueva Orden</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" /></div>
        ) : viewMode === 'kanban' ? (
          <div className={cn('grid gap-4', `grid-cols-1 md:grid-cols-3 lg:grid-cols-${activeCols.length}`)}>
            {activeCols.map(col => (
              <div key={col.id} className="space-y-2" onDrop={e => handleDrop(e, col.id)} onDragOver={e => e.preventDefault()} data-testid={`wo-column-${col.id}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${col.color}`} /><h3 className={cn('font-medium text-sm', isDark ? 'text-white' : 'text-zinc-800')}>{col.label}</h3>
                  <span className="text-xs text-zinc-500 ml-auto">{orders.filter(o => o.status === col.id).length}</span>
                </div>
                <div className={cn('space-y-2 min-h-[200px] p-2 rounded-xl border', isDark ? 'bg-[#141414] border-white/5' : 'bg-zinc-50 border-zinc-100')}>
                  {orders.filter(o => o.status === col.id).map(o => <OrderCard key={o.work_order_id} order={o} />)}
                  {orders.filter(o => o.status === col.id).length === 0 && <div className="flex flex-col items-center justify-center h-24 text-zinc-500"><ClipboardList size={20} className="mb-1" /><p className="text-xs">Sin ordenes</p></div>}
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'weekly' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(d); }} className="text-zinc-400"><ChevronLeft size={18} /></Button>
              <span className={cn('font-medium', isDark ? 'text-white' : 'text-zinc-900')}>{format(currentWeekStart, "d MMM", { locale: es })} - {format(new Date(currentWeekStart.getTime() + 6 * 86400000), "d MMM yyyy", { locale: es })}</span>
              <Button variant="ghost" size="sm" onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(d); }} className="text-zinc-400"><ChevronRight size={18} /></Button>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 7 }, (_, i) => {
                const day = new Date(currentWeekStart.getTime() + i * 86400000);
                const dayStr = day.toISOString().split('T')[0];
                const dayOrders = orders.filter(o => o.due_date && o.due_date.split('T')[0] === dayStr);
                const isToday = new Date().toISOString().split('T')[0] === dayStr;
                return (
                  <div key={i} className={cn('rounded-xl p-3 min-h-[200px]', isToday ? 'bg-orange-500/5 border border-orange-500/20' : isDark ? 'bg-[#141414] border border-white/5' : 'bg-zinc-50 border border-zinc-100')}>
                    <p className={cn('text-xs font-medium mb-2', isToday ? 'text-orange-400' : 'text-zinc-400')}>{format(day, 'EEE d', { locale: es })}</p>
                    <div className="space-y-2">{dayOrders.map(o => <div key={o.work_order_id} className={cn('p-2 rounded-lg border cursor-pointer', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-white border-zinc-200')} onClick={() => openDetail(o)}><p className={cn('text-xs font-medium truncate', isDark ? 'text-white' : 'text-zinc-900')}>{o.title}</p><span className={cn('px-1.5 py-0.5 rounded text-[10px]', priorityColor[o.priority])}>{priorityLabel[o.priority]}</span></div>)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="text-zinc-400"><ChevronLeft size={18} /></Button>
              <span className={cn('font-medium capitalize', isDark ? 'text-white' : 'text-zinc-900')}>{format(currentMonth, 'MMMM yyyy', { locale: es })}</span>
              <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="text-zinc-400"><ChevronRight size={18} /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'].map(d => <div key={d} className="text-center text-xs text-zinc-500 py-2 font-medium">{d}</div>)}
              {(() => {
                const year = currentMonth.getFullYear(); const month = currentMonth.getMonth();
                const firstDay = new Date(year, month, 1); const lastDay = new Date(year, month + 1, 0);
                let startDay = firstDay.getDay() || 7; const days = [];
                for (let i = 1; i < startDay; i++) days.push(null);
                for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
                const todayStr = new Date().toISOString().split('T')[0];
                return days.map((day, idx) => {
                  if (!day) return <div key={`e-${idx}`} className="min-h-[70px]" />;
                  const dayStr = day.toISOString().split('T')[0];
                  const dayOrders = orders.filter(o => o.due_date && o.due_date.split('T')[0] === dayStr);
                  const isToday = dayStr === todayStr;
                  return (
                    <div key={dayStr} className={cn('min-h-[70px] rounded-lg p-1.5', isToday ? 'bg-orange-500/5 border border-orange-500/20' : isDark ? 'bg-[#141414] border border-white/5' : 'bg-zinc-50 border border-zinc-100')}>
                      <p className={cn('text-[10px] font-medium mb-1', isToday ? 'text-orange-400' : 'text-zinc-500')}>{day.getDate()}</p>
                      {dayOrders.slice(0, 2).map(o => <div key={o.work_order_id} className={cn('text-[10px] truncate rounded px-1 py-0.5 mb-0.5 cursor-pointer', isDark ? 'text-zinc-300 bg-white/5' : 'text-zinc-600 bg-zinc-100')} onClick={() => openDetail(o)}>{o.title}</div>)}
                      {dayOrders.length > 2 && <p className="text-[10px] text-orange-400">+{dayOrders.length - 2}</p>}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {renderCreateDialog()}
        {renderDetailDialog()}
        {renderRejectDialog()}
        {lightbox && <Lightbox files={lightbox.files} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className={cn('max-w-lg max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
            <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Editar Orden de Trabajo</DialogTitle></DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Titulo</Label>
                <Input value={editForm.title || ''} onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))} className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} required data-testid="wo-edit-title" />
              </div>
              <div>
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Descripcion</Label>
                <Textarea value={editForm.description || ''} onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))} className={cn('min-h-[80px]', isDark ? 'bg-[#1A1A1A] border-transparent' : '')} data-testid="wo-edit-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Prioridad</Label>
                  <Select value={editForm.priority || 'medium'} onValueChange={v => setEditForm(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} data-testid="wo-edit-priority"><SelectValue /></SelectTrigger>
                    <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>
                      <SelectItem value="low">Baja</SelectItem><SelectItem value="medium">Media</SelectItem><SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Tipo de trabajo</Label>
                  <Select value={editForm.work_type || 'none'} onValueChange={v => setEditForm(prev => ({ ...prev, work_type: v === 'none' ? '' : v }))}>
                    <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} data-testid="wo-edit-worktype"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>
                      <SelectItem value="none">Ninguno</SelectItem>
                      {WORK_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Fecha de entrega</Label>
                <Popover open={editDatePickerOpen} onOpenChange={setEditDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('w-full justify-start text-left font-normal', isDark ? 'bg-[#1A1A1A] border-transparent' : '', !editForm.due_date && 'text-zinc-500')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />{editForm.due_date ? format(new Date(editForm.due_date), 'PPP HH:mm', { locale: es }) : 'Selecciona fecha'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className={cn('w-auto p-0', isDark ? 'bg-[#1A1A1A] border-white/10' : '')}>
                    <Calendar mode="single" selected={editForm.due_date ? new Date(editForm.due_date) : undefined} onSelect={d => { setEditForm(prev => ({ ...prev, due_date: d })); setEditDatePickerOpen(false); }} locale={es} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="wo-edit-submit">Guardar Cambios</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Convert / Assign Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <DialogContent className={cn('max-w-sm', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
            <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Asignar a Empleado</DialogTitle></DialogHeader>
            <form onSubmit={handleConvert} className="space-y-4">
              <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Empleado</Label>
                <Select value={convertForm.assigned_to} onValueChange={v => setConvertForm({ assigned_to: v })}>
                  <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} data-testid="assign-employee-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>{team.filter(t => t.role !== 'client').map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={!convertForm.assigned_to} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="assign-submit-btn">Asignar</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default WorkOrdersPage;
